import React, { useMemo } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import { AutoSizer, Table, Column, List } from 'react-virtualized';
import 'react-virtualized/styles.css';

const STATUS_COLOURS = {
    'Completed': '#4CAF50',
    'Completed Late': '#FFB300',
    'Not Completed': '#f44336',
    'Not Yet Due': '#90CAF9',
};

const STATUS_TEXT_COLOURS = {
    'Completed': '#fff',
    'Completed Late': '#000',
    'Not Completed': '#fff',
    'Not Yet Due': '#000',
};

const formatDateTime = (val) => {
    if (!val) return '';
    const d = new Date(val);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const hrs = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month} ${hrs}:${mins}`;
};

const toDateKey = (val) => {
    if (!val) return null;
    const d = new Date(val);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const cellRenderer = ({ cellData }) => (
    <span className="tagging-detail__cell">{cellData}</span>
);

const statusCellRenderer = ({ cellData }) => (
    <span
        className="tagging-detail__status-badge"
        style={{
            backgroundColor: STATUS_COLOURS[cellData] || '#666',
            color: STATUS_TEXT_COLOURS[cellData] || '#fff',
        }}
    >
        {cellData}
    </span>
);

const dateTimeCellRenderer = ({ cellData }) => (
    <span className="tagging-detail__cell">{formatDateTime(cellData)}</span>
);

const lateCellRenderer = ({ cellData }) => (
    <span className={cellData > 0 ? 'tagging-detail__late-value' : ''}>
        {cellData > 0 ? cellData : ''}
    </span>
);

const DetailGrid = ({ data, siteFilter, activeFilter, onRowClick }) => {
    const isMobileView = isMobile || isTablet;

    const filteredData = useMemo(() => {
        let result = data || [];

        if (siteFilter) {
            result = result.filter(d => d.siteName === siteFilter);
        }

        if (activeFilter && activeFilter.source !== 'detail') {
            result = result.filter(row => {
                if (activeFilter.site && row.siteName !== activeFilter.site) return false;
                if (activeFilter.task) {
                    const rowTask = row.taskGroupDescription || row.taskDescription || '';
                    if (rowTask !== activeFilter.task) return false;
                }
                if (activeFilter.staff && row.staffName !== activeFilter.staff) return false;
                if (activeFilter.status && row.taskStatus !== activeFilter.status) return false;
                if (activeFilter.date) {
                    const rowDate = toDateKey(row.dueDate);
                    if (rowDate !== activeFilter.date) return false;
                }
                return true;
            });
        }

        return result;
    }, [data, siteFilter, activeFilter]);

    const emitRowClick = (row) => {
        onRowClick?.({
            site: row.siteName,
            task: row.taskDescription,
            staff: row.staffName,
            date: toDateKey(row.dueDate),
        });
    };

    if (filteredData.length === 0) {
        return (
            <div className="tagging-detail__empty">
                {activeFilter ? 'No tasks match the current filter.' : 'No data available.'}
            </div>
        );
    }

    if (isMobileView) {
        const cardRenderer = ({ index, key, style }) => {
            const row = filteredData[index];
            return (
                <div key={key} style={style} onClick={() => emitRowClick(row)}>
                    <div className="tagging-detail__card">
                        <div className="tagging-detail__card-header">
                            <span className="tagging-detail__card-site">{row.siteName}</span>
                            <span
                                className="tagging-detail__status-badge"
                                style={{
                                    backgroundColor: STATUS_COLOURS[row.taskStatus] || '#666',
                                    color: STATUS_TEXT_COLOURS[row.taskStatus] || '#fff',
                                }}
                            >
                                {row.taskStatus}
                            </span>
                        </div>
                        <div className="tagging-detail__card-task">{row.taskGroupDescription || row.taskDescription}</div>
                        <div className="tagging-detail__card-fields">
                            <div className="tagging-detail__card-field">
                                <span className="tagging-detail__card-label">Staff</span>
                                <span>{row.staffName}</span>
                            </div>
                            <div className="tagging-detail__card-field">
                                <span className="tagging-detail__card-label">Due</span>
                                <span>{formatDateTime(row.dateTimeToDo)}</span>
                            </div>
                            <div className="tagging-detail__card-field">
                                <span className="tagging-detail__card-label">Done</span>
                                <span>{formatDateTime(row.dateTimeCompleted)}</span>
                            </div>
                            {row.minsLate > 0 && (
                                <div className="tagging-detail__card-field">
                                    <span className="tagging-detail__card-label">Late</span>
                                    <span className="tagging-detail__late-value">{row.minsLate} mins</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        };

        return (
            <div className="tagging-detail__virtualised-cards">
                <AutoSizer>
                    {({ width, height }) => (
                        <List
                            width={width}
                            height={height}
                            rowCount={filteredData.length}
                            rowHeight={120}
                            rowRenderer={cardRenderer}
                            overscanRowCount={5}
                        />
                    )}
                </AutoSizer>
            </div>
        );
    }

    const rowGetter = ({ index }) => filteredData[index];

    const headerRenderer = ({ label }) => (
        <span className="tagging-detail__header">{label}</span>
    );

    const isDetailSource = activeFilter?.source === 'detail';

    const rowClassName = ({ index }) => {
        if (index < 0) return 'tagging-detail__header-row';
        const base = index % 2 === 0 ? 'tagging-detail__row--even' : 'tagging-detail__row--odd';
        if (isDetailSource) {
            const row = filteredData[index];
            const match = row.siteName === activeFilter.site
                && row.taskDescription === activeFilter.task
                && row.staffName === activeFilter.staff
                && toDateKey(row.dueDate) === activeFilter.date;
            if (match) return `${base} tagging-detail__row--active`;
        }
        return base;
    };

    return (
        <div className="tagging-detail__virtualised">
            <AutoSizer>
                {({ width, height }) => (
                    <Table
                        width={width}
                        height={height}
                        rowHeight={36}
                        rowCount={filteredData.length}
                        rowGetter={rowGetter}
                        headerHeight={38}
                        rowClassName={rowClassName}
                        overscanRowCount={10}
                        onRowClick={({ index }) => emitRowClick(filteredData[index])}
                        rowStyle={{ cursor: 'pointer' }}
                    >
                        <Column width={150} minWidth={120} flexGrow={1} label="Site" dataKey="siteName" cellRenderer={cellRenderer} headerRenderer={headerRenderer} />
                        <Column width={140} minWidth={100} flexGrow={1} label="Task Group" dataKey="taskGroupDescription" cellRenderer={cellRenderer} headerRenderer={headerRenderer} />
                        <Column width={140} minWidth={100} flexGrow={1} label="Task" dataKey="taskDescription" cellRenderer={cellRenderer} headerRenderer={headerRenderer} />
                        <Column width={110} minWidth={80} flexGrow={1} label="Staff" dataKey="staffName" cellRenderer={cellRenderer} headerRenderer={headerRenderer} />
                        <Column width={110} minWidth={90} label="Status" dataKey="taskStatus" cellRenderer={statusCellRenderer} headerRenderer={headerRenderer} />
                        <Column width={95} minWidth={80} label="Due" dataKey="dateTimeToDo" cellRenderer={dateTimeCellRenderer} headerRenderer={headerRenderer} />
                        <Column width={95} minWidth={80} label="Completed" dataKey="dateTimeCompleted" cellRenderer={dateTimeCellRenderer} headerRenderer={headerRenderer} />
                        <Column width={70} minWidth={60} label="Late (mins)" dataKey="minsLate" cellRenderer={lateCellRenderer} headerRenderer={headerRenderer} />
                    </Table>
                )}
            </AutoSizer>
        </div>
    );
};

export default DetailGrid;
